import {
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus,
  InputAddBranchQuoteSupport,
  SupportQuoteItemLinkDetail
} from '../../api/dealer-api-interface-franchisee';
import { ConversationActionTemplate } from './conversation-types';
import { customElement, property, query, state } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { Editor } from '@toast-ui/editor';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import { getApiFactory } from '../../api/api-injector';
import { getSupportStatusBadge, getSupportTypeDisplayValue } from './conversation-helper';
import { html, PropertyValueMap } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { lockUIandExecute } from '../../../webmodule-common/other/ui-lock';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { newStandardEditor } from '../../../webmodule-common/other/ui/toastui';
import { showValidations } from '../../../webmodule-common/other/ui/modal-validationhandler';
import { LangBranchQuoteSupportNew } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
import { WebModuleConversationEntryDropZone } from './wm-conversation-entry-dropzone';
import { QuoteItemSelectorModal } from '../quotes/views/franchisee-quote-item-table';

export interface SupportNewActions {
  cancel: ConversationActionTemplate;
  post: ConversationActionTemplate;
  draft: ConversationActionTemplate;
}

@customElement('wmview-branchquote-support-new')
export class WebModuleBranchQuoteSupportNew extends LitElementBase {
  protected dataBinding = new DataBinding(this);
  protected dataTracker = new DataTracker(this.dataBinding);

  @property() quoteManager?: FranchiseeQuoteContainerManager;

  get quoteId(): string | undefined {
    return this.quoteManager?.branchQuote.id;
  }
  get quoteSetId(): string | undefined {
    return this.quoteManager?.branchQuote.quoteSetId;
  }
  @property() supportType: BranchQuoteSupportItemType = BranchQuoteSupportItemType.QuoteAssistanceRequest;
  @state()
  isUploading = false;
  @property() showButtons = true;
  @property() showPostButton = true;
  @property() showCancelButton = true;

  @property()
  public get text() {
    return this.editor?.getMarkdown() ?? '';
  }
  @property() branchQuoteSupportId = newGuid();
  @property() conversationEntryId = newGuid();

  @property() subjectField?: string;

  @property() supplierName = '';

  textEditor: HTMLElement = document.createElement('div');
  @query('#attachments')
  attachmentsEditor?: WebModuleConversationEntryDropZone;
  protected editor?: Editor;

  constructor() {
    super();
    this.prepareBindings();
  }

  prepareBindings() {
    this.dataTracker.addObjectBinding(() => this, 'subjectField', 'subjectField', FieldType.string, true);
  }

  get subject() {
    if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();
    return this.subjectField ?? '';
  }

  @state()
  protected linkedQuoteItemKeys: string[] = [];
  makeDisplayValueForQuoteItemId(id: string) {
    return this.quoteManager?.makeSupportLinkDisplayValueForQuoteItemId(id) ?? '';
  }

  protected getQuoteItemLinks(): Record<string, SupportQuoteItemLinkDetail> {
    return this.linkedQuoteItemKeys.reduce<Record<string, SupportQuoteItemLinkDetail>>((acc, key) => {
      acc[key] = { isLinked: true, id: key, textDisplay: this.makeDisplayValueForQuoteItemId(key) };
      return acc;
    }, {});
  }

  protected getInput(draft = false): InputAddBranchQuoteSupport {
    if (!this.quoteId) throw new DevelopmentError('Support Ticket must have quoteid');
    return {
      quoteItemLink: this.getQuoteItemLinks(),
      id: this.branchQuoteSupportId,
      masterDocumentConversationEntry: null,
      conversationId: this.branchQuoteSupportId,
      branchQuoteId: this.quoteId,
      status: draft ? BranchQuoteSupportStatus.Draft : BranchQuoteSupportStatus.New,
      subject: this.subject,
      type: this.supportType,
      conversationEntry: {
        id: this.conversationEntryId,
        attachments: this.attachmentsEditor?.allFilesAsInputs ?? [],
        text: this.text,
        users: []
      }
    };
  }
  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (this.textEditor && !this.editor) {
      this.editor = newStandardEditor(this.textEditor);
    }
  }
  protected eventCancelEntry = async (e: CustomEvent) => {
    e.stopImmediatePropagation();
    this.dispatchCustom('cancel', {});
  };

  public getValidations(): string[] {
    const result: string[] = [];
    if (isEmptyOrSpace(this.subject)) {
      result.push(
        LangBranchQuoteSupportNew.pleaseAddSupportSubject({ supportType: getSupportTypeDisplayValue(this.supportType) })
      );
    }
    if (isEmptyOrSpace(this.text)) {
      // message specified via PBI 218417
      result.push(
        LangBranchQuoteSupportNew.pleaseProvideSupportInitialTopic({
          supportType: getSupportTypeDisplayValue(this.supportType),
          supplierName: this.supplierName
        })
      );
    }
    return result;
  }

  public async canSave(): Promise<boolean> {
    const validation = this.getValidations();
    return validation.length == 0;
  }

  protected async internalCreateEntry(draft = false) {
    await lockUIandExecute(async () => {
      const result = await getApiFactory().franchisee().addBranchQuoteSupport(this.getInput(draft));
      if (result)
        this.dispatchCustom('created', {
          branchQuoteSupport: result.branchQuoteSupport
        });
    });
    this.requestUpdate();
  }
  public async save(): Promise<boolean> {
    if (await this.canSave()) {
      if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();
      await this.internalCreateEntry(true);
      return true;
    }
    return false;
  }
  protected eventCreateEntry = async (_e: CustomEvent<{ draft: boolean }>) => {
    const valid = this.getValidations();
    if (valid.length > 0) {
      await showValidations(valid);
      return;
    }
    await this.internalCreateEntry();
  };
  protected eventCreateDraftEntry = async (_e: CustomEvent<{ draft: boolean }>) => {
    const valid = this.getValidations();
    if (valid.length > 0) {
      await showValidations(valid);
      return;
    }
    await this.internalCreateEntry(true);
  };

  connectedCallback(): void {
    super.connectedCallback();
    // this.subjectEditor?.focus();
  }

  get forceReadonly() {
    return this.quoteManager?.isReadonly() ?? true;
  }

  render() {
    const readonly = this.forceReadonly;
    const forms = new FormInputAssistant(this.dataTracker, readonly);

    const mainHeader = html`<div
      class="main-header d-flex justify-content-between  border-bottom border-secondary mb-2"
    >
      <h2>${LangBranchQuoteSupportNew.supportRequest()} ${getSupportStatusBadge(BranchQuoteSupportStatus.Draft)}</h2>
    </div> `;

    return html`
      <div class="new-support-ticket form-two-col">
        <div class="row">
          <div class="eto-left form-column2">
            ${mainHeader}
            <div class="conversation conversation-entry-create">
              <div class="conversation-header mb-3">
                <div class="d-flex conversation-subject-wrapper">
                  ${forms.text1('subjectField', LangBranchQuoteSupportNew.supportSubject(), { maxLength: 100 })}
                </div>
                <div class="d-flex conversation-link-wrapper">
                  ${forms.multiEditRow({
                    title: LangBranchQuoteSupportNew.linkQuoteItems(),
                    toolTip: LangBranchQuoteSupportNew.linkQuoteItemsLabel(),
                    slotted: html`<webmodule-button
                      size="small"
                      variant="primary"
                      @click=${this.showOrSelectLinkedItems}
                    >
                      ${LangBranchQuoteSupportNew.linkQuoteItems()}
                    </webmodule-button>`
                  })}
                </div>
              </div>
              ${this.conversationNoteHeader()} ${this.addNoteTemplate()} ${this.attachmentsTemplate()}
              ${this.footerTemplate()}
            </div>
          </div>
          <div class="eto-right form-column">
            <!-- Empty, to mimic structure of editor page -->
          </div>
        </div>
      </div>
    `;
  }
  conversationNoteHeader(): unknown {
    const title = LangBranchQuoteSupportNew.supportInitialTopic();

    return html`<div class="pb-1 mb-1 mt-2 border-bottom border-secondary">
      <h2 class="text-primary">${title}</h2>
    </div>`;
  }

  protected eventUploadingChanged = (e: CustomEvent<{ isUploading: boolean }>) => {
    this.isUploading = e.detail.isUploading;
    this.dispatchCustom('changed', { isUploading: this.isUploading });
  };
  attachmentsTemplate(): unknown {
    return html` <wmview-conversation-attachments
      id="attachments"
      @wm-event-changed=${this.eventUploadingChanged}
      .conversationId=${this.branchQuoteSupportId}
      .conversationEntryId=${this.conversationEntryId}
    ></wmview-conversation-attachments>`;
  }

  actionsTemplate(): SupportNewActions {
    const result: SupportNewActions = {
      draft: (_hide?: boolean) => {
        return html`<webmodule-button
          size="small"
          variant="default"
          ?disabled=${this.isUploading}
          @click=${this.eventCreateDraftEntry}
        >
          ${LangBranchQuoteSupportNew.saveDraft()}
        </webmodule-button>`;
      },
      post: (_hide?: boolean) => {
        return html`<webmodule-button
          size="small"
          variant="default"
          ?disabled=${this.isUploading}
          @click=${this.eventCreateEntry}
        >
          ${LangBranchQuoteSupportNew.issueTicket()}
        </webmodule-button>`;
      },
      cancel: () => {
        return html`<webmodule-button
          size="small"
          variant="default"
          ?disabled=${this.isUploading}
          @click=${this.eventCancelEntry}
        >
          ${LangBranchQuoteSupportNew.cancel()}
        </webmodule-button>`;
      }
    };

    return result;
  }

  footerTemplate(): unknown {
    const actions = this.actionsTemplate();

    return this.showButtons
      ? html`<div class="conversation-list-actions">
          ${this.showPostButton ? actions.post() : ''}${this.showCancelButton ? actions.cancel() : ''}
        </div>`
      : html``;
  }
  addNoteTemplate(): unknown {
    return this.textEditor;
  }
  async showOrSelectLinkedItems() {
    if (!this.quoteManager) return;
    const modal = new QuoteItemSelectorModal(this.quoteManager!, this.linkedQuoteItemKeys);
    await modal.showModal();
    if (modal.result?.success) {
      this.linkedQuoteItemKeys = modal.result.keys;
    }
  }
}
